/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */
/**
 *
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @version 1.12.0
 * @since 1.1.0
 */
import { Fragment } from 'react';

import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { IFixedColumn, IFixedColumns } from '../../../../../modules/app/hoc/WithFixedColumns/helpers';
import { IRootReducer } from '../../../../../reducers/rootReducer';
import { formatBalance } from '../../../../../utils/formatText';
import { TABLE_WIDTH_MIN } from '../../../constants';
import { addSpecialSignToFilter } from '../../../helpers/tableColumn';
import { ITableReducer } from '../../../reducers/tableReducer';
import { makeSelectorTableCurrentName, makeSelectorTableFooter } from '../../../selectors/tableSelectors';
import { ColumnFieldType, ColumnTranslationType, EColumnCell } from '../../../types/columnTypes';
import { MapColumnNameToFooterName } from '../../../types/tableMapTypes';
import { TableFooterType, TableNameType } from '../../../types/tableTypes';
import { IColumn } from '../Columns';

export interface ITableColumnFooter {
    data: {
        column: IFixedColumn;
        columns: IFixedColumns;
    };
}

export const TableColumnFooter = <Field extends ColumnFieldType, Translation extends ColumnTranslationType>
(props: IColumn<Field, Translation> & ITableColumnFooterConnectedProps & ITableColumnFooter) => {
    const firstFixed = props.data.columns.filter((column: IFixedColumn) => {
        const width: number = (typeof column.width === 'string' ? parseInt(column.width) : column.width) || 0;
        return column.fixed && (Math.max(width, column.minWidth ?? 0)) >= TABLE_WIDTH_MIN;
    })?.[0] || props.data.columns?.[0];

    let value = '';
    const mapFieldsToFooter = MapColumnNameToFooterName[props.tableName];
    if (mapFieldsToFooter && mapFieldsToFooter[props.name]) {
        const name = mapFieldsToFooter[props.name];
        value = props.tableFooter?.[name as keyof TableFooterType] ?? '';
    }
    if (!value && firstFixed && firstFixed.id === props.data.column.id) {
        value = props.translation.corporateTable.total;
    }
    if (props.cellType === EColumnCell.amount) {
        value = formatBalance(parseFloat(value))
            + addSpecialSignToFilter(EColumnCell.balance, props.translation, parseFloat(value));
    }

    return (
        <Fragment>
            {
                value ?
                    <div>
                        {value}
                    </div>
                    :
                    null
            }
        </Fragment>
    );
};

interface ITableColumnFooterConnectedProps {
    tableFooter: ITableReducer[TableNameType]['tableFooter'];
    tableName: TableNameType;
}

const mapStateToProps = createStructuredSelector<IRootReducer, ITableColumnFooterConnectedProps>({
    tableName: makeSelectorTableCurrentName(),
    tableFooter: makeSelectorTableFooter(),
});

export default connect(mapStateToProps)(TableColumnFooter);


